import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  IconButton,
  useMediaQuery,
  useTheme,
  Link,
  Stack,
  Paper,
  Chip,
  Popover,
  Tooltip,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SendIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useQuery, useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  fetchTrainingHub,
  createTrainingAssistantSession,
  chatTrainingAssistant,
} from "../api/client";

/** Painel de aba que ocupa o espaço restante sem criar rolagem extra no DialogContent. */
function TabPanel({ children, value, index, dense }) {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pt: dense ? 0.5 : 2,
        px: dense ? { xs: 0.75, sm: 1 } : { xs: 1.5, sm: 2 },
        pb: dense ? 0.5 : 1,
      }}
    >
      {children}
    </Box>
  );
}

const sortedModules = (mods) =>
  [...(mods || [])].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));

const sortedItems = (items) =>
  [...(items || [])].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));

const kindLabel = (k) => {
  const s = String(k || "").toLowerCase();
  if (s === "pdf") return "PDF";
  if (s === "video") return "Vídeo";
  if (s === "image") return "Imagem";
  if (s === "link") return "Link";
  return s || "Arquivo";
};

const HELP_ONBOARDING_NAV_COLLAPSED_KEY = "xpro.helpOnboarding.navCollapsed";

function readStoredNavCollapsed() {
  try {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(HELP_ONBOARDING_NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function persistNavCollapsed(collapsed) {
  try {
    window.localStorage.setItem(HELP_ONBOARDING_NAV_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    /* quota / modo privado */
  }
}

const HelpOnboardingDialog = ({ open, onClose, onStartTour }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [selectedTrainingIdx, setSelectedTrainingIdx] = useState(0);
  const [selectedModuleIdx, setSelectedModuleIdx] = useState(0);
  const [selectedItemIdx, setSelectedItemIdx] = useState(0);
  const [mainTab, setMainTab] = useState(0);

  const [sessionId, setSessionId] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [input, setInput] = useState("");
  const [contextAnchor, setContextAnchor] = useState(null);
  /** Oculta a coluna de navegação para maximizar PDF/vídeo/chat (preferência persistida em localStorage). */
  const [navCollapsed, setNavCollapsed] = useState(() => readStoredNavCollapsed());
  const listRef = useRef(null);

  const { data: trainings = [], isLoading, isError, refetch } = useQuery(
    ["trainingHub"],
    () => fetchTrainingHub(),
    { enabled: open, staleTime: 30_000, refetchOnWindowFocus: false }
  );

  const selectedTraining = trainings[selectedTrainingIdx] || null;
  const modules = sortedModules(selectedTraining?.training?.modules);
  const selectedModule = modules[selectedModuleIdx] || null;
  const items = sortedItems(selectedModule?.items || []);
  const selectedItem = items[selectedItemIdx] || null;

  useEffect(() => {
    if (!open) return;
    setSelectedModuleIdx(0);
    setSelectedItemIdx(0);
  }, [selectedTrainingIdx, open]);

  useEffect(() => {
    if (!open) setContextAnchor(null);
  }, [open]);

  useEffect(() => {
    setSelectedItemIdx(0);
  }, [selectedModuleIdx]);

  useEffect(() => {
    if (open && selectedTrainingIdx >= trainings.length && trainings.length > 0) {
      setSelectedTrainingIdx(0);
    }
  }, [open, trainings.length, selectedTrainingIdx]);

  const sessionMutation = useMutation(
    async (trainingId) => createTrainingAssistantSession({ trainingId }),
    {
      onSuccess: (data) => {
        if (!data) return;
        setSessionId(String(data?.sessionId || ""));
        const hello = String(data?.message || "").trim();
        setChatMessages(hello ? [{ role: "assistant", content: hello }] : []);
      },
    }
  );

  useEffect(() => {
    if (!open) {
      setSessionId("");
      setChatMessages([]);
      setInput("");
      return;
    }
    if (!selectedTraining?.id) {
      setSessionId("");
      setChatMessages([]);
      return;
    }
    setSessionId("");
    setChatMessages([]);
    sessionMutation.mutate(selectedTraining.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTraining?.id]);

  const chatMutation = useMutation(async ({ userMessage, history }) =>
    chatTrainingAssistant({ sessionId, userMessage, history })
  );

  useEffect(() => {
    if (!listRef.current) return;
    try {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    } catch {
      /* ignore */
    }
  }, [chatMessages, chatMutation.isLoading]);

  const handleSend = async () => {
    const msg = String(input || "").trim();
    if (!msg || chatMutation.isLoading || !sessionId) return;
    const next = [...chatMessages, { role: "user", content: msg }];
    setChatMessages(next);
    setInput("");
    try {
      const history = next.slice(-12);
      const resp = await chatMutation.mutateAsync({ userMessage: msg, history });
      const reply =
        String(resp?.reply || resp?.message || "").trim() || "Não consegui responder no momento.";
      setChatMessages((cur) => [...cur, { role: "assistant", content: reply }]);
    } catch (e) {
      const emsg =
        (typeof e?.message === "string" && e.message) ||
        (typeof e?.error === "string" && e.error) ||
        "Não foi possível conversar com a IA no momento.";
      setChatMessages((cur) => [...cur, { role: "assistant", content: emsg }]);
    }
  };

  /** Ocupa todo o espaço vertical disponível (pai deve ser flex column com flex:1 minHeight:0). */
  const renderMaterialViewport = () => {
    if (!selectedItem?.url) {
      return (
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 200,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            textAlign: "center",
            bgcolor: "action.hover",
          }}
        >
          <MenuBookIcon sx={{ fontSize: 36, color: "text.secondary", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Escolha um material à esquerda: Treinamento → Módulo → Material.
          </Typography>
        </Paper>
      );
    }

    const kind = String(selectedItem.kind || "pdf").toLowerCase();
    const url = selectedItem.url;
    const title = selectedItem.title || "Material";

    const stageSx = {
      flex: 1,
      minHeight: 0,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      bgcolor: kind === "video" ? "common.black" : "grey.100",
    };

    if (kind === "video") {
      return (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            position: "relative",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "common.black",
            overflow: "hidden",
          }}
        >
          <video
            controls
            src={url}
            title={title}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          >
            <track kind="captions" />
          </video>
        </Box>
      );
    }

    if (kind === "image") {
      return (
        <Box sx={{ ...stageSx, overflow: "auto", justifyContent: "flex-start", p: 1 }}>
          <Box
            component="img"
            src={url}
            alt={title}
            sx={{
              maxWidth: "100%",
              width: "auto",
              height: "auto",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
              mx: "auto",
            }}
          />
        </Box>
      );
    }

    if (kind === "pdf") {
      return (
        <Box sx={{ ...stageSx, p: 0, bgcolor: "grey.200", display: "flex", flexDirection: "column" }}>
          <Box component="iframe" title={title} src={url} sx={{ flex: 1, width: "100%", minHeight: 0, border: 0 }} />
        </Box>
      );
    }

    return (
      <Paper variant="outlined" sx={{ flex: 1, minHeight: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 3, gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Link ({kindLabel(kind)})
        </Typography>
        <Button component={Link} href={url} target="_blank" rel="noopener noreferrer" variant="contained" endIcon={<OpenInNewIcon />}>
          Abrir em nova aba
        </Button>
      </Paper>
    );
  };

  const emptyTrainings = !isLoading && !isError && trainings.length === 0;

  const dialogPaperSx = {
    maxHeight: isSmDown ? "100%" : "min(960px, 98vh)",
    height: isSmDown ? "100%" : "min(960px, 98vh)",
    display: "flex",
    flexDirection: "column",
    m: isSmDown ? 0 : 0.5,
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isSmDown}
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
          flexShrink: 0,
          py: 0.75,
          px: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <SchoolIcon color="primary" sx={{ fontSize: 22 }} />
        <Typography component="span" variant="subtitle1" fontWeight={700}>
          Ajuda e Treinamento
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            startIcon={<MenuBookIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              onClose?.();
              navigate("/help");
            }}
            sx={{ py: 0.25, minHeight: 32 }}
          >
            Guia completo
          </Button>
          <Button size="small" variant="outlined" onClick={() => onStartTour?.()} sx={{ py: 0.25, minHeight: 32 }}>
            Tour guiado
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        {isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Carregando materiais…</Typography>
          </Box>
        )}

        {isError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" action={<Button onClick={() => refetch()}>Tentar de novo</Button>}>
              Não foi possível carregar os treinamentos.
            </Alert>
          </Box>
        )}

        {emptyTrainings && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">
              Nenhum treinamento publicado ainda. Use <strong>Guia completo</strong> (canto superior) para entender
              cada área do sistema, ou inicie o <strong>Tour guiado</strong>. Administradores podem cadastrar materiais
              em{" "}
              <Link component="button" variant="body2" onClick={() => navigate("/contents")}>
                Conteúdos
              </Link>{" "}
              (tipo &quot;Treinamento da plataforma&quot;).
            </Alert>
          </Box>
        )}

        {!isLoading && trainings.length > 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: isSmDown ? "column" : "row",
              overflow: "hidden",
            }}
          >
            {/* Navegação: uma única coluna com rolagem (pode ser ocultada — modo foco) */}
            {!navCollapsed ? (
            <Paper
              elevation={0}
              square
              sx={{
                width: isSmDown ? "100%" : 216,
                maxWidth: "100%",
                flexShrink: 0,
                borderRight: isSmDown ? 0 : 1,
                borderBottom: isSmDown ? 1 : 0,
                borderColor: "divider",
                bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.50"),
                display: "flex",
                flexDirection: "column",
                maxHeight: isSmDown ? "32vh" : "none",
                minHeight: isSmDown ? 0 : 0,
              }}
            >
              <Box sx={{ overflowY: "auto", flex: 1, px: 0.75, py: 0.75 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ letterSpacing: 0.4, fontSize: "0.65rem", lineHeight: 1.2, display: "block", mb: 0.25 }}
                >
                  1 · Treinamento
                </Typography>
                <List dense disablePadding sx={{ mb: 0.5 }}>
                  {trainings.map((t, idx) => (
                    <ListItemButton
                      key={t.id || idx}
                      selected={selectedTrainingIdx === idx}
                      onClick={() => setSelectedTrainingIdx(idx)}
                      sx={{ borderRadius: 0.75, py: 0.35, px: 1, mb: 0.125 }}
                    >
                      <ListItemText
                        primary={t.titulo || `Treinamento ${idx + 1}`}
                        secondary={t.slug ? `/${t.slug}` : null}
                        primaryTypographyProps={{ variant: "body2", fontWeight: selectedTrainingIdx === idx ? 600 : 400 }}
                      />
                    </ListItemButton>
                  ))}
                </List>

                {modules.length > 0 && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ letterSpacing: 0.4, fontSize: "0.65rem", lineHeight: 1.2, display: "block", mb: 0.25 }}
                    >
                      2 · Módulo
                    </Typography>
                    <List dense disablePadding sx={{ mb: 0.5 }}>
                      {modules.map((m, idx) => (
                        <ListItemButton
                          key={m.id || idx}
                          selected={selectedModuleIdx === idx}
                          onClick={() => setSelectedModuleIdx(idx)}
                          sx={{ borderRadius: 0.75, py: 0.35, px: 1, mb: 0.125 }}
                        >
                          <ListItemText primary={m.title || `Módulo ${idx + 1}`} primaryTypographyProps={{ variant: "body2" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}

                {items.length > 0 && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ letterSpacing: 0.4, fontSize: "0.65rem", lineHeight: 1.2, display: "block", mb: 0.25 }}
                    >
                      3 · Material
                    </Typography>
                    <List dense disablePadding>
                      {items.map((it, idx) => (
                        <ListItemButton
                          key={it.id || idx}
                          selected={selectedItemIdx === idx}
                          onClick={() => setSelectedItemIdx(idx)}
                          sx={{ borderRadius: 0.75, py: 0.35, px: 1, mb: 0.125 }}
                        >
                          <ListItemText
                            primary={it.title || `${it.kind || "item"} ${idx + 1}`}
                            secondary={kindLabel(it.kind)}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}
              </Box>
            </Paper>
            ) : null}

            {/* Conteúdo principal: abas + uma zona de rolagem por aba */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: isSmDown && !navCollapsed ? "40vh" : 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "stretch",
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Tabs
                  value={mainTab}
                  onChange={(_, v) => setMainTab(v)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 0.5,
                    minHeight: 40,
                    "& .MuiTab-root": { minHeight: 40, py: 0.5, px: 1, fontSize: "0.8125rem" },
                  }}
                >
                  <Tab label="Ver materiais" icon={<MenuBookIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                  <Tab label="Tirar dúvidas" icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                </Tabs>
                <Tooltip title={navCollapsed ? "Mostrar menu (treinamentos e materiais)" : "Foco no material (ocultar menu)"}>
                  <IconButton
                    onClick={() => {
                      setNavCollapsed((c) => {
                        const next = !c;
                        persistNavCollapsed(next);
                        return next;
                      });
                    }}
                    aria-expanded={!navCollapsed}
                    aria-label={navCollapsed ? "Mostrar menu lateral" : "Ocultar menu lateral"}
                    sx={{
                      alignSelf: "stretch",
                      borderRadius: 0,
                      borderLeft: 1,
                      borderColor: "divider",
                      px: 0.75,
                    }}
                  >
                    {navCollapsed ? <MenuOpenIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <TabPanel value={mainTab} index={0} dense>
                  <Stack spacing={0.5} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={0.5}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ flexShrink: 0, rowGap: 0.25 }}
                    >
                      <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        {selectedTraining?.titulo && (
                          <Chip size="small" label={selectedTraining.titulo} color="primary" variant="outlined" sx={{ height: 24, "& .MuiChip-label": { px: 0.75, py: 0, fontSize: "0.75rem" } }} />
                        )}
                        {selectedModule?.title && (
                          <Chip size="small" label={selectedModule.title} variant="outlined" sx={{ height: 24, "& .MuiChip-label": { px: 0.75, py: 0, fontSize: "0.75rem" } }} />
                        )}
                        {selectedItem?.title && (
                          <Chip size="small" label={selectedItem.title} variant="filled" color="primary" sx={{ height: 24, "& .MuiChip-label": { px: 0.75, py: 0, fontSize: "0.75rem" } }} />
                        )}
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                        {selectedItem?.url ? (
                          <Tooltip
                            title={
                              String(selectedItem.kind || "").toLowerCase() === "pdf"
                                ? "Abrir PDF em nova aba (recomendado)"
                                : "Abrir em nova aba"
                            }
                          >
                            <IconButton
                              component={Link}
                              href={selectedItem.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              color="primary"
                              aria-label="Abrir material em nova aba"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Como usar, introdução e notas">
                          <IconButton
                            size="small"
                            onClick={(e) => setContextAnchor(e.currentTarget)}
                            aria-label="Como usar e contexto"
                            aria-haspopup="true"
                            aria-expanded={Boolean(contextAnchor)}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      {renderMaterialViewport()}
                    </Box>
                  </Stack>
                </TabPanel>

                <TabPanel value={mainTab} index={1}>
                  <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                    <Alert severity="info" variant="outlined" sx={{ flexShrink: 0, py: 0.75 }}>
                      A IA responde com base neste treinamento (textos e notas dos materiais). Pode errar — confira sempre
                      os PDFs e vídeos na aba &quot;Ver materiais&quot;.
                    </Alert>
                    {sessionMutation.isLoading && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", flexShrink: 0 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Preparando assistente…</Typography>
                      </Box>
                    )}
                    {sessionMutation.isError && (
                      <Alert severity="warning" sx={{ flexShrink: 0 }}>
                        Não foi possível iniciar o assistente para este treinamento.
                      </Alert>
                    )}
                    <Box
                      ref={listRef}
                      sx={{
                        flex: 1,
                        minHeight: 160,
                        overflowY: "auto",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50"),
                      }}
                    >
                      {chatMessages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Pergunte sobre o treinamento <strong>{selectedTraining?.titulo || ""}</strong>. Ex.: &quot;Quais
                          são os passos para…?&quot;
                        </Typography>
                      ) : (
                        <Stack spacing={1.25}>
                          {chatMessages.map((m, idx) => (
                            <Box
                              key={idx}
                              sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "94%" }}
                            >
                              <Box
                                sx={{
                                  px: 1.25,
                                  py: 1,
                                  borderRadius: 2,
                                  bgcolor: m.role === "user" ? "primary.main" : "background.paper",
                                  color: m.role === "user" ? "primary.contrastText" : "text.primary",
                                  boxShadow: m.role === "user" ? 0 : 1,
                                  border: m.role === "user" ? 0 : 1,
                                  borderColor: "divider",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <Typography variant="body2">{m.content}</Typography>
                              </Box>
                            </Box>
                          ))}
                          {chatMutation.isLoading && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2">Pensando…</Typography>
                            </Box>
                          )}
                        </Stack>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, pt: 0.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Escreva sua dúvida…"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        disabled={!sessionId || sessionMutation.isLoading}
                      />
                      <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!input.trim() || chatMutation.isLoading || !sessionId}
                        sx={{ flexShrink: 0 }}
                      >
                        <SendIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </TabPanel>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0, px: 1.5, py: 0.5, borderTop: 1, borderColor: "divider" }}>
        <Button variant="contained" size="small" onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>

      <Popover
        open={Boolean(contextAnchor)}
        anchorEl={contextAnchor}
        onClose={() => setContextAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 420, p: 1.5 },
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Como usar e contexto
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          À esquerda, escolha o treinamento, o módulo e o material. O conteúdo ocupa a área principal. Para PDFs, abrir em
          nova aba costuma ser mais confortável que o visualizador embutido.
        </Typography>
        {selectedTraining?.training?.intro?.trim() ? (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Sobre este treinamento
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {selectedTraining.training.intro}
            </Typography>
          </>
        ) : null}
        {selectedItem?.notes?.trim() ? (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Resumo / notas deste material
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {selectedItem.notes}
            </Typography>
          </>
        ) : null}
      </Popover>
    </Dialog>
  );
};

export default HelpOnboardingDialog;
